
import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  getTourPrograms,
  deleteTourProgram,
  exportTourPrograms,
} from "services/api/tourProgramApi";

import { getEmployees } from "services/api/employeeApi";
import Select from "react-select";

import TourProgramModal from "./TourProgramModal";
import { showSuccess, showError } from "utils/toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PAGE_SIZE = 10;

const INITIAL_FILTERS = {
  employeeName: "",
  fromDate: "",
  toDate: "",
};

const inputClass =
  "h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white";

const buttonClass =
  "inline-flex h-11 items-center justify-center rounded-lg px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const dateText = (value) => {
  if (!value) return "-";

  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");

  return parts.length === 3
    ? `${parts[2]}-${parts[1]}-${parts[0]}`
    : raw;
};

const getErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;

  return Array.isArray(message)
    ? message.join(", ")
    : message || fallback;
};

export default function TourPrograms({ isAdmin = false }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });

  const [filters, setFilters] = useState({
    ...INITIAL_FILTERS,
  });

  const [appliedFilters, setAppliedFilters] = useState({
    ...INITIAL_FILTERS,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selected, setSelected] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  // Both Admin and Salesperson use the same list endpoint.
  // Backend must decide which records the logged-in user can see.
  
const loadData = useCallback(async () => {
  try {
    setLoading(true);

    const params = {
      page,
      limit: PAGE_SIZE,
    };

    // Filters are available only in the Admin screen.
    if (isAdmin) {
      if (appliedFilters.employeeName.trim()) {
        params.employeeName =
          appliedFilters.employeeName.trim();
      }

      if (appliedFilters.fromDate) {
        params.fromDate = appliedFilters.fromDate;
      }

      if (appliedFilters.toDate) {
        params.toDate = appliedFilters.toDate;
      }
    }

    const response = await getTourPrograms(params);

    const body = response?.data;
    const list = Array.isArray(body?.data)
      ? body.data
      : [];

    setRecords(list);

    if (isAdmin) {
      // Admin pagination is handled by the backend.
      const total = Number(
        body?.pagination?.total ?? list.length
      );

      const totalPages = Number(
        body?.pagination?.totalPages ??
          Math.max(1, Math.ceil(total / PAGE_SIZE))
      );

      setPagination({
        total,
        totalPages: Math.max(1, totalPages),
      });
    } else {
      // /mine returns all records, so paginate locally.
      setPagination({
        total: list.length,
        totalPages: Math.max(
          1,
          Math.ceil(list.length / PAGE_SIZE)
        ),
      });
    }
  } catch (error) {
    console.error("Tour Programs load error:", error);

    setRecords([]);
    setPagination({
      total: 0,
      totalPages: 1,
    });

    const message = error?.response?.data?.message;

    showError(
      Array.isArray(message)
        ? message.join(", ")
        : message || "Unable to load Tour Programs."
    );
  } finally {
    setLoading(false);
  }
}, [isAdmin, page, appliedFilters]);

const loadEmployees = async () => {
  try {

    setEmployeeLoading(true);

    const response = await getEmployees(1,500);


    const list = response?.data?.data?.employees || [];

    setEmployees(list);

  } catch(error){

    console.error(
      "Employee loading error",
      error
    );

    setEmployees([]);

  } finally {

    setEmployeeLoading(false);

  }
};

useEffect(() => {
  loadData();
}, [loadData]);

useEffect(() => {

 if(isAdmin){
    loadEmployees();
 }

},[isAdmin]);


  // The unified API is server-paginated for both roles.
  
const visibleRecords = isAdmin
  ? records
  : records.slice(
      (page - 1) * PAGE_SIZE,
      page * PAGE_SIZE
    );


  // Filter handlers
  const updateFilter = (key, value) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const applyFilters = () => {
    if (
      filters.fromDate &&
      filters.toDate &&
      filters.fromDate > filters.toDate
    ) {
      showError("From Date cannot be later than To Date.");
      return;
    }

    setPage(1);

    setAppliedFilters({
      employeeName: filters.employeeName.trim(),
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    });
  };

  const clearFilters = () => {
    setFilters({ ...INITIAL_FILTERS });
    setAppliedFilters({ ...INITIAL_FILTERS });
    setPage(1);
  };

  // Modal handlers
  const openModal = (mode, record = null) => {
    setSelected(record);
    setModalMode(mode);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelected(null);
    setModalMode("create");
  };

  // Delete
  const removeRecord = async (record) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the Tour Program for ${dateText(
        record.tourDate
      )}?`
    );

    if (!confirmed) return;

    try {
      await deleteTourProgram(record.id);

      showSuccess("Tour Program deleted successfully.");

      if (visibleRecords.length === 1 && page > 1) {
        setPage((current) => Math.max(1, current - 1));
      } else {
        await loadData();
      }
    } catch (error) {
      showError(
        getErrorMessage(
          error,
          "Unable to delete Tour Program."
        )
      );
    }
  };

  // Excel export: all matching records, not only the current page.
  const downloadExcel = async () => {
    try {
      setExporting(true);

      const params = {};

      if (appliedFilters.employeeName.trim()) {
        params.employeeName =
          appliedFilters.employeeName.trim();
      }

      if (appliedFilters.fromDate) {
        params.fromDate = appliedFilters.fromDate;
      }

      if (appliedFilters.toDate) {
        params.toDate = appliedFilters.toDate;
      }

      const response = await exportTourPrograms(params);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `tour-programs-${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

      showSuccess("Tour Programs Excel exported successfully.");
    } catch (error) {
      console.error("Tour Programs export error:", error);

      showError(
        getErrorMessage(
          error,
          "Unable to export Tour Programs."
        )
      );
    } finally {
      setExporting(false);
    }
  };

  
const [exportingPdf, setExportingPdf] = useState(false);

const downloadPDF = async () => {
  try {
    setExportingPdf(true);

    // Export every page matching the applied filters.
    const allRecords = [];
    let currentPage = 1;
    let totalPagesToFetch = 1;

    do {
      const params = {
        page: currentPage,
        limit: 100,
      };

      if (appliedFilters.employeeName.trim()) {
        params.employeeName = appliedFilters.employeeName.trim();
      }

      if (appliedFilters.fromDate) {
        params.fromDate = appliedFilters.fromDate;
      }

      if (appliedFilters.toDate) {
        params.toDate = appliedFilters.toDate;
      }

      const response = await getTourPrograms(params);
      const body = response?.data;
      const list = Array.isArray(body?.data) ? body.data : [];

      allRecords.push(...list);

      totalPagesToFetch = Number(
        body?.pagination?.totalPages ?? 1
      );

      currentPage += 1;
    } while (currentPage <= totalPagesToFetch);

    if (!allRecords.length) {
      showError("No Tour Programs found for PDF export.");
      return;
    }

    // Sort records chronologically.
    allRecords.sort(
      (a, b) =>
        new Date(a.tourDate).getTime() -
        new Date(b.tourDate).getTime()
    );

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const formatDate = (value) => {
      if (!value) return "";

      const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
      if (Number.isNaN(date.getTime())) return "";

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    };

    const getDay = (value) => {
      if (!value) return "";

      const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

      return Number.isNaN(date.getTime())
        ? ""
        : date.toLocaleDateString("en-IN", { weekday: "long" });
    };

    const moneyValue = (value) => Number(value || 0);

    const formatMoney = (value) =>
      moneyValue(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const firstRecord = allRecords[0];

    const employeeName =
      appliedFilters.employeeName ||
      firstRecord.employeeName ||
      "All Employees";

    const monthSource =
      appliedFilters.fromDate ||
      firstRecord.tourDate;

    const monthDate = monthSource
      ? new Date(`${String(monthSource).slice(0, 10)}T00:00:00`)
      : new Date();

    const monthName = monthDate.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });

    // Header
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text("INSPIRATION PUBLICATION (INDIA)", 105, 15, {
      align: "center",
    });

    pdf.setFontSize(11);
    pdf.text("TENTATIVE TOUR PROGRAM", 105, 22, {
      align: "center",
    });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);

    pdf.text(`SALES PERSON NAME: ${employeeName}`, 14, 32);
    pdf.text(`Month: ${monthName}`, 150, 32);

    // Form table
    const rows = allRecords.map((record) => [
      formatDate(record.tourDate),
      getDay(record.tourDate),
      record.destination || "",
      formatMoney(record.daAmount),
      formatMoney(record.taAmount),
      formatMoney(
        record.totalAmount ??
          moneyValue(record.daAmount) + moneyValue(record.taAmount)
      ),
    ]);

    const grandDA = allRecords.reduce(
      (sum, record) => sum + moneyValue(record.daAmount),
      0
    );

    const grandTA = allRecords.reduce(
      (sum, record) => sum + moneyValue(record.taAmount),
      0
    );

    const grandTotal = allRecords.reduce(
      (sum, record) =>
        sum +
        moneyValue(
          record.totalAmount ??
            moneyValue(record.daAmount) + moneyValue(record.taAmount)
        ),
      0
    );

    autoTable(pdf, {
      startY: 38,
      head: [["DATE", "DAY", "PLACE", "DA", "T.A.", "TOTAL"]],
      body: rows,
      foot: [[
        { content: "GRAND T.", colSpan: 3, styles: { halign: "left" } },
        formatMoney(grandDA),
        formatMoney(grandTA),
        formatMoney(grandTotal),
      ]],
      theme: "grid",
      margin: { left: 10, right: 10, bottom: 12 },
      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 2.5,
        lineColor: [130, 130, 130],
        lineWidth: 0.15,
        textColor: [30, 30, 30],
        overflow: "linebreak",
      },
      headStyles: {
        fontStyle: "bold",
        fillColor: [255, 255, 255],
        textColor: [30, 30, 30],
        halign: "center",
      },
      footStyles: {
        fontStyle: "bold",
        fillColor: [255, 255, 255],
        textColor: [30, 30, 30],
      },
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 27 },
        2: { cellWidth: 49 },
        3: { cellWidth: 25, halign: "right" },
        4: { cellWidth: 25, halign: "right" },
        5: { cellWidth: 30, halign: "right" },
      },
    });

    const safeName = employeeName
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase();

    pdf.save(`tour-program-${safeName}-${monthName.replace(/\s+/g, "-")}.pdf`);

    showSuccess("Tour Program PDF exported successfully.");
  } catch (error) {
    console.error("Tour Program PDF export error:", error);

    showError(
      getErrorMessage(error, "Unable to export Tour Program PDF.")
    );
  } finally {
    setExportingPdf(false);
  }
};


  const totalPages = Math.max(1, pagination.totalPages);

  return (
    <div className="mt-5 space-y-5">
      <div className="rounded-2xl bg-white p-5 shadow-sm dark:bg-navy-800">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-navy-700 dark:text-white">
              Tour Programs
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              {isAdmin
                ? "View and filter tour plans submitted by salespersons."
                : "Manage your tour dates, destinations and allowances."}
            </p>
          </div>

      
            <button
              type="button"
              onClick={() => openModal("create")}
              className={`${buttonClass} bg-brand-500 text-white hover:bg-brand-600`}
            >
              + Add Tour Program
            </button>
        
        </div>

        {/* Admin filters */}
        {isAdmin && (
          <div className="mb-6 rounded-xl border border-gray-200 p-4 dark:border-navy-600">
            <h3 className="mb-4 text-base font-bold text-navy-700 dark:text-white">
              Filter Tour Programs
            </h3>

            <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-2 xl:grid-cols-[1.2fr_1.2fr_1.2fr_1.2fr_auto_auto]">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Employee Name
                </label>

                <Select
                  inputId="tour-program-employee-filter"
                  isSearchable
                  isClearable
                  isLoading={employeeLoading}
                  options={employees
                    .map((emp) => {
                      const name = String(
                        emp?.user?.name ??
                        emp?.employeeName ??
                        emp?.name ??
                        emp?.employee?.name ??
                        ""
                      ).trim();

                      return name
                        ? {
                            value: name,
                            label: name,
                          }
                        : null;
                    })
                    .filter(Boolean)
                    .filter(
                      (option, index, options) =>
                        options.findIndex(
                          (item) => item.value.toLowerCase() === option.value.toLowerCase()
                        ) === index
                    )}
                  value={
                    filters.employeeName
                      ? {
                          value: filters.employeeName,
                          label: filters.employeeName,
                        }
                      : null
                  }
                  onChange={(selectedOption) =>
                    updateFilter(
                      "employeeName",
                      selectedOption?.value ?? ""
                    )
                  }
                  placeholder={employeeLoading ? "Loading employees..." : "Search employee..."}
                  noOptionsMessage={() => "No employees found"}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: 44,
                      borderRadius: 8,
                      borderColor: state.isFocused ? "#4318ff" : "#e2e8f0",
                      boxShadow: state.isFocused ? "0 0 0 1px #4318ff" : "none",
                      backgroundColor: "var(--tour-select-bg, #ffffff)",
                      "&:hover": {
                        borderColor: "#4318ff",
                      },
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "0 8px",
                    }),
                    input: (base) => ({
                      ...base,
                      color: "#374151",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#374151",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#6b7280",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  From Date
                </label>

                <input
                  type="date"
                  value={filters.fromDate}
                  max={filters.toDate || undefined}
                  onChange={(event) =>
                    updateFilter("fromDate", event.target.value)
                  }
                  className={inputClass}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  To Date
                </label>

                <input
                  type="date"
                  value={filters.toDate}
                  min={filters.fromDate || undefined}
                  onChange={(event) =>
                    updateFilter("toDate", event.target.value)
                  }
                  className={inputClass}
                />
              </div>

              <button
                type="button"
                onClick={applyFilters}
                disabled={loading}
                className={`${buttonClass} bg-brand-500 text-white hover:bg-brand-600`}
              >
                Search
              </button>

              
             
<div className="flex items-end gap-2">
  {isAdmin && (
    <>
      <button
        type="button"
        onClick={downloadExcel}
        disabled={exporting}
        className={`${buttonClass} border border-green-200 bg-green-50 text-green-700 hover:bg-green-100`}
      >
        {exporting ? "Exporting..." : "Export Excel"}
      </button>

      <button
        type="button"
        onClick={downloadPDF}
        disabled={exportingPdf || loading}
        className={`${buttonClass} border border-red-200 bg-red-50 text-red-700 hover:bg-red-100`}
      >
        {exportingPdf ? "Generating PDF..." : "Export PDF"}
      </button>
    </>
  )}
</div>

            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Excel export includes all records matching the applied filters.
              </p>

              <button
                type="button"
                onClick={clearFilters}
                disabled={loading}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                {isAdmin && (
                  <th className="px-3 py-3">Employee</th>
                )}

                <th className="px-3 py-3">Tour Date</th>
                <th className="px-3 py-3">Destination</th>
                <th className="px-3 py-3 text-right">TA Amount</th>
                <th className="px-3 py-3 text-right">DA Amount</th>
                <th className="px-3 py-3 text-right">Total Amount</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={isAdmin ? 8 : 7}
                    className="py-12 text-center text-sm text-gray-500"
                  >
                    Loading Tour Programs...
                  </td>
                </tr>
              ) : visibleRecords.length === 0 ? (
                <tr>
                  <td
                    colSpan={isAdmin ? 8 : 7}
                    className="py-12 text-center text-sm text-gray-500"
                  >
                    No Tour Programs found.
                  </td>
                </tr>
              ) : (
                visibleRecords.map((record) => (
                  <tr
                    key={record.id}
                    className="border-b border-gray-100 text-sm hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700"
                  >
                    {isAdmin && (
                      <td className="px-3 py-4">
                        <div className="font-semibold text-gray-800 dark:text-white">
                          {record.employeeName || "-"}
                        </div>

                        <div className="text-xs text-gray-500">
                          {record.employeeCode || "-"}
                        </div>
                      </td>
                    )}

                    <td className="px-3 py-4">
                      {dateText(record.tourDate)}
                    </td>

                    <td className="px-3 py-4 font-medium text-gray-800 dark:text-white">
                      {record.destination || "-"}
                    </td>

                    <td className="px-3 py-4 text-right">
                      {money(record.taAmount)}
                    </td>

                    <td className="px-3 py-4 text-right">
                      {money(record.daAmount)}
                    </td>

                    <td className="px-3 py-4 text-right font-bold text-gray-800 dark:text-white">
                      {money(record.totalAmount)}
                    </td>

                    <td className="px-3 py-4">
                      <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold capitalize text-blue-700">
                        {record.status || "submitted"}
                      </span>
                    </td>

                    <td className="px-3 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openModal("view", record)}
                          className="rounded-md border px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:text-gray-200"
                        >
                          View
                        </button>

                        {!isAdmin && (
                          <>
                            {record.status === "submitted" && (
                              <button
                                type="button"
                                onClick={() => openModal("edit", record)}
                                className="rounded-md border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                              >
                                Edit
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => removeRecord(record)}
                              className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            Total records: {pagination.total}
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() =>
                setPage((current) => Math.max(1, current - 1))
              }
              className="rounded-lg border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>

            <span className="px-2 text-sm text-gray-600 dark:text-gray-300">
              Page {page} of {totalPages}
            </span>

            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
              className="rounded-lg border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Create / Edit / View modal */}
      <TourProgramModal
        open={modalOpen}
        onClose={closeModal}
        onSaved={loadData}
        item={selected}
        mode={modalMode}
        isAdmin={isAdmin}
      />
    </div>
  );
}
